import type { FC, ReactNode } from 'react'

import { motion } from 'motion/react'
import { cn } from '@/lib/utils'
import { duration, enter } from '@/lib/motion'
import { SplitText } from '@/components/motion/split-text'

interface HeroHeadingProps {
    first: string
    connector: string
    second: string
    align?: 'center' | 'start'
    tone?: 'dark' | 'light'
    className?: string
}

// The one expressive moment on each page: each line rises out of its own mask, then settles. No overshoot.
export const HeroHeading: FC<HeroHeadingProps> = ({
    first,
    connector,
    second,
    align = 'center',
    tone = 'dark',
    className
}): ReactNode => {
    return (
        <h1
            aria-label={`${first} ${connector} ${second}`}
            className={cn(
                'flex select-none flex-col overflow-clip',
                align === 'center' ? 'items-center' : 'items-start',
                tone === 'light' ? 'text-cream' : 'text-foreground',
                className
            )}
        >
            <span className='block overflow-clip'>
                <motion.span
                    className='block'
                    initial={{ y: '100%' }}
                    animate={{ y: 0 }}
                    transition={enter(0.1, duration.hero)}
                >
                    <SplitText
                        text={first}
                        effect='rise'
                        trigger='mount'
                        delay={0.1}
                        stagger={0.02}
                        accessible={false}
                        className='t-display block whitespace-pre'
                    />
                </motion.span>
            </span>

            {' '}

            <span className='flex w-min items-center gap-1 overflow-clip md:gap-4'>
                <motion.span
                    className='block'
                    initial={{ y: '100%' }}
                    animate={{ y: 0 }}
                    transition={enter(0.25, duration.hero)}
                >
                    <SplitText
                        text={connector}
                        effect='fade'
                        trigger='mount'
                        delay={0.25}
                        accessible={false}
                        className='t-display-serif block whitespace-pre'
                    />
                </motion.span>

                {' '}

                <motion.span
                    className='block'
                    initial={{ y: '100%' }}
                    animate={{ y: 0 }}
                    transition={enter(0.35, duration.hero)}
                >
                    <SplitText
                        text={second}
                        effect='rise'
                        trigger='mount'
                        delay={0.35}
                        stagger={0.02}
                        accessible={false}
                        className='t-display block whitespace-pre'
                    />
                </motion.span>
            </span>
        </h1>
    )
}
